import { Dispatch, SetStateAction } from 'react';
import { MessageDescriptor } from 'react-intl';
import { UseQueryResult } from '@tanstack/react-query';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarAlt,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import { faCalendarExclamation } from '@fortawesome/pro-regular-svg-icons';

export enum Route {
  root = '/',
  search = '/search',
  accountActivation = '/account-activation',
  advancedSearch = '/advanced-search',
  login = '/login',
  directLogin = '/direct-login',
  projects = '/projects',
  projectsDetails = '/projects/:id',
  projectsDetailsSearches = '/projects/:id/searches',
  projectsDetailsRecords = '/projects/:id/records',
  createAccount = '/create-account',
  preregistration = '/preregistration',
  resendVerificationLink = '/preregistration/resend-verification-link',
  logout = '/logout',
  instanceDetails = '/instances/:id',
  requestsAndLoans = '/requests-and-loans',
  holdsAndCheckouts = '/holds-and-checkouts',
  holds = '/holds',
  checkouts = '/checkouts',
  requests = '/requests',
  loans = '/loans',
  fees = '/fees',
  courseReserves = '/course-reserves',
  courseReserveDetails = '/course-reserves/:id',
  sso = '/sso-landing',
  callNumbers = '/call-numbers',
  preferences = '/my-preferences',
  notFound = '/not-found',
  resetPinRoot = '/reset-pin',
  enterNewPin = '/reset-pin/pin',
  enterBarcode = '/reset-pin/barcode',
  recentActivity = '/recent-activity',
  recentSearchHistory = '/recent-activity/search-history',
  recentViewedRecords = '/recent-activity/viewed-records',
  saved = '/saved',
  savedSearches = '/saved/searches',
  savedRecords = '/saved/records',
}

export const ssoRedirectRouteKey = 'redirectRoute';

export enum Feature {
  holdsAndCheckouts = 'HOLDS_AND_CHECKOUTS',
  courseReserves = 'COURSE_RESERVES',
  staffView = 'STAFF_VIEW',
  virtualBookshelf = 'VIRTUAL_BOOKSHELF',
  novelist = 'NOVELIST',
  callNumbers = 'CALL_NUMBERS',
  instanceRelationship = 'INSTANCE_RELATIONSHIP',
  statusSearchFacet = 'STATUS_SEARCH_FACET',
  renewHold = 'RENEW_HOLD',
  placeHold = 'PLACE_HOLD',
  placeHoldNotes = 'PLACE_HOLD_NOTES',
  publicationYearFacet = 'PUBLICATION_YEAR_FACET',
  consortiaLoginFlow = 'CONSORTIA_LOGIN_FLOW',
  RTACForSearch = 'RTAC_FOR_SEARCH',
  LCCNSearch = 'LCCN_SEARCH',
  passthroughlink = 'PASSTHROUGH_LINK',
  preregistration = 'PREREGISTRATION',
  placeHoldLibraryNote = 'PLACE_HOLD_LIBRARY_NOTE',
  LCLinkResolver = 'LC_LINK_RESOLVER',
  LCAdvancedSearchOptions = 'LC_ADVANCED_SEARCH_OPTIONS',
  LCBrowseShelfAndRelatedSubjectsPlacement = 'LC_BROWSE_SHELF_AND_RELATED_SUBJECTS_PLACEMENT',
  downloadMarc = 'DOWNLOAD_MARC',
  LCDashboard = 'LC_DASHBOARD',
  LCAdvancedSearchPlaceOfPublication = 'LC_ADVANCED_SEARCH_PLACE_OF_PUBLICATION',
  LCRTACTable = 'LC_RTAC_TABLE',
}

export interface FeatureFlagToMatch {
  name: Feature;
  isActiveIfNotFound?: boolean;
}

export enum UserSession {
  guest = 'guestUserSession',
  patron = 'patronUserSession',
}

export const breakpoint = {
  CONTAINER: {
    MINIMUM_SCREEN_SIZE: 274,
    PHONE: 382,
    TABLET_SM: 738,
    TABLET_LG: 960,
    DESKTOP_SM: 1180,
    DESKTOP_MD: 1428,
    DESKTOP_LG: 1600,
  },
  SCREEN: {
    MINIMUM_SCREEN_SIZE: 0,
    PHONE: 411,
    TABLET_SM: 767,
    TABLET_LG: 1023,
    DESKTOP_SM: 1239,
    DESKTOP_MD: 1499,
    DESKTOP_LG: 1919,
  },
};

export const supportSiteUrl = 'https://connect.ebsco.com/s/?language=en_US';

export enum SortDirection {
  asc = 'ascending',
  desc = 'descending',
}

export enum SortDirectionReversed {
  ascending = 'asc',
  descending = 'desc',
}

export const advancedSearchError = {
  code: 'validation_error',
  type: 'RequestValidationException',
};

export const backToTopTriggerScrollPosition = 300;

export const minSuggestionTermLength = 3;

export const isTestEnv = process.env.NODE_ENV === 'test';

export const defaultRecordsPerPage = isTestEnv ? 2 : 25;

export const rtacTableRecordsPerPage = isTestEnv ? 3 : 8;

export const totalRecordsLimit = isTestEnv ? 8 : 10000;

export const modalPlacement = isTestEnv ? 'body > div' : '#root';

export const loginMethods = {
  sso: 'SSO only',
  native: 'Native only',
};

export interface URLQuery {
  [key: string]: string | string[] | number;
}

export interface SortOption {
  label: MessageDescriptor;
  sortField: string;
  sortDirection?: SortDirection;
  icon?: IconDefinition;
}

export enum NotificationStatus {
  success = 'success',
  error = 'error',
  information = 'information',
}

export const focusedElementSelector = '[data-focus=focusedNode]';

export enum DefaultCoverImageTypes {
  'Books' = 'book',
  'Videos' = 'video',
  'Electronic Resources' = 'eBook',
  'Audio' = 'audio',
  'nonCatalog' = 'nonCatalog',
}

export const englishLocaleKey = 'en';

export type ShapeWithTotalRecords<P = Record<string, unknown>> = P & {
  totalRecords: number;
};

export type ShapeWithClassName<P = Record<string, unknown>> = P & {
  className?: string;
};

export enum LoginMethod {
  native = 'native',
  direct = 'direct',
  sso = 'sso',
}

export enum PasswordType {
  pin = 'pin',
  password = 'password',
}

export const invokePathPrefix = `/_/invoke/tenant/${process.env.REACT_APP_API_TENANT}`;

export type SetState<Type> = Dispatch<SetStateAction<Type>>;

export const searchInterface = 'LOCATE Catalog';

export interface InstanceItemPublication {
  dateOfPublication: string;
  publisher?: string;
  place?: string;
  role?: string;
}

export interface InstanceItem {
  instanceId: string;
  itemId: string;
  title: string;
  authors?: string[];
  isbns?: string[];
  sourceTypes?: string[];
  publication?: InstanceItemPublication[];
  virtual?: boolean;
}

export interface HoldDto {
  item: InstanceItem;
  pickupLocationId: string;
  requestDate: string;
  requestId: string;
  status: string;
  queuePosition?: number;
}

export interface CheckoutDto {
  dueDate: string;
  id: string;
  item: InstanceItem;
  loanDate: string;
  overdue: boolean;
  amount?: number;
  isoCurrencyCode?: string;
}

export type ItemWithStatus = HoldDto | CheckoutDto;

export interface ChargeDto {
  accrualDate: string;
  state: string;
  reason: string;
  chargeAmount: {
    amount: number;
    isoCurrencyCode: string;
  };
  item?: InstanceItem;
}

export interface ItemStatusDto {
  totalHolds: number;
  totalLoans: number;
  totalChargesCount: number;
  totalCharges: {
    amount: number;
    isoCurrencyCode: string;
  };
  holds?: HoldDto[];
  loans?: CheckoutDto[];
  charges?: ChargeDto[];
}

export interface ItemStatusData {
  itemId: string;
  icon: IconDefinition;
  status: string;
  isNonCatalogItem?: boolean;
  instanceId?: string;
  actionButtonText?: string;
  statusMessage?: string;
  feesTitle?: string;
  feesValue?: string;
  holdId?: string;
  isVirtualItem?: boolean;
}

export interface DepartmentInstructorItemDto {
  id: string;
  name: string;
}

export enum ItemStatus {
  onHold = 'On hold',
  checkedOut = 'Checked out',
  overdue = 'Overdue',
}

export const statusIconMap = {
  [ItemStatus.onHold]: faCalendarAlt,
  [ItemStatus.checkedOut]: faCalendarCheck,
  [ItemStatus.overdue]: faCalendarExclamation,
};

export type Resource<PageType, QueryResultType> = ShapeWithTotalRecords<{
  pages: PageType[][];
  recordKey: keyof PageType;
  queryResult: UseQueryResult<QueryResultType>;
  next?: string;
}>;

export const quickFilterType = {
  allTime: 'allTime',
  pastYear: 'pastYear',
  pastFiveYears: 'pastFiveYears',
  pastTenYears: 'pastTenYears',
  custom: 'custom',
} as const;

export const supportedLocales = [
  { type: 'English', label: 'English', id: 'en' },
  { type: 'German', label: 'Deutsch', id: 'de' },
  { type: 'Spanish', label: 'Español', id: 'es' },
  { type: 'Greek', label: 'Ελληνικά', id: 'el' },
  { type: 'French', label: 'Français', id: 'fr' },
  { type: 'Italian', label: 'Italiano', id: 'it' },
  { type: 'Hungarian', label: 'Magyar', id: 'hu' },
  { type: 'Polish', label: 'Polski', id: 'pl' },
  { type: 'Portuguese (Brasilia)', label: 'Português (Brasil)', id: 'pt-BR' },
  {
    type: 'Portuguese (Portuguese)',
    label: 'Português (Portugal)',
    id: 'pt-PT',
  },
  { type: 'Russian', label: 'Русский', id: 'ru' },
  { type: 'Turkish', label: 'Türkçe', id: 'tr' },
  { type: 'Arabic', label: 'عربي', id: 'ar' },
  { type: 'Simplified Chinese', label: '简体中文', id: 'zh-CN' },
  { type: 'Traditional Chinese', label: '繁體中文', id: 'zh-TW' },
  { type: 'Japanese', label: '日本語', id: 'ja' },
  { type: 'Korean', label: '한국어', id: 'ko' },
  { type: 'Thai', label: 'ภาษาไทย', id: 'th' },
  { type: 'Vietnamese', label: 'Tiếng Việt', id: 'vi' },
  { type: 'Croatian', label: 'Hrvatski', id: 'hr' },
  { type: 'Czech', label: 'Čeština', id: 'cs' },
  { type: 'Indonesian', label: 'Bahasa Indonesia', id: 'id' },
  { type: 'Hebrew', label: 'עִבְרִית', id: 'he' },
  { type: 'Romanian', label: 'Română', id: 'ro' },
  { type: 'Slovenian', label: 'Slovenski', id: 'sl' },
  { type: 'Slovak', label: 'Slovenčina', id: 'sk' },
  { type: 'Dutch', label: 'Nederlands', id: 'nl' },
  { type: 'Swedish', label: 'Svenska', id: 'sv' },
  { type: 'Farsi', label: 'فارسی', id: 'fa' },
  { type: 'Finnish', label: 'Suomi', id: 'fi' },
  { type: 'Danish', label: 'Dansk', id: 'da' },
  { type: 'Norwegian', label: 'Norsk', id: 'no' },
];

export type IntlMessages = Record<string, string>;

export const holdsAndCheckoutsTabs = {
  holds: 'holds',
  checkouts: 'checkouts',
  fees: 'fees',
} as const;

export type HoldsAndCheckoutsTab = keyof typeof holdsAndCheckoutsTabs;

export const defaultDebounceTimeMs = isTestEnv ? 0 : 300;

export const pageDefaults = {
  pageNumber: 1,
  recordsPerPage: defaultRecordsPerPage,
};

export const storageKeys = {
  searchHistory: 'searchHistory',
  savedInstances: 'savedInstances',
  savedSearches: 'savedSearches',
  viewedRecords: 'viewedRecords',
  locale: 'locale',
};

export const emailValidationRegex = /[\w\-.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const accountActivationStatus = {
  activated: 'ACCOUNT_ACTIVATED',
};
